import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ISetFilterParams,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core';
import { round } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import { VariationStatusComponent } from '@features/variation-status/variation-status.component';

import { TableConstants } from '@shared/constants/table.constants';
import { ExpenseType } from '@shared/services/gql.service';
import { BudgetCurrencyType } from '@shared/store/budget-currency/budget-currency.model';
import { AuxExcelStyleKeys } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { VisibleColumns } from './column-chooser-component/column-chooser.component';
import { agBudgetCurrencyFormatter } from './utils/budget-formatters.utils';

export enum CellSize {
  large = 125,
  medium = 100,
  small = 75,
  xLarge = 150,
}

export const uomHide$ = new BehaviorSubject(true);

export const period_sorting = [
  Utils.SHORT_MONTH_NAMES[0],
  Utils.SHORT_MONTH_NAMES[1],
  Utils.SHORT_MONTH_NAMES[2],
  'Q1',
  Utils.SHORT_MONTH_NAMES[3],
  Utils.SHORT_MONTH_NAMES[4],
  Utils.SHORT_MONTH_NAMES[5],
  'Q2',
  Utils.SHORT_MONTH_NAMES[6],
  Utils.SHORT_MONTH_NAMES[7],
  Utils.SHORT_MONTH_NAMES[8],
  'Q3',
  Utils.SHORT_MONTH_NAMES[9],
  Utils.SHORT_MONTH_NAMES[10],
  Utils.SHORT_MONTH_NAMES[11],
  'Q4',
];

export const getCellClass =
  (selectedBudgetCurrencyType: BudgetCurrencyType) => (params: CellClassParams) => {
    if (
      selectedBudgetCurrencyType === BudgetCurrencyType.VENDOR &&
      params?.node?.data?.contract_direct_cost_currency
    ) {
      return `budgetCost${params?.node?.data?.contract_direct_cost_currency}`;
    } else if (selectedBudgetCurrencyType === BudgetCurrencyType.USD) {
      return 'budgetCostUSD';
    }

    return 'budgetCostNoSymbol';
  };

const percentageFormatter = (val: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  let fVal = Utils.zeroHyphen;

  if (val) {
    fVal = `${formatter.format(val)}%`;
  }

  return fVal;
};

const shouldHideUnits = (params: ValueFormatterParams): boolean => {
  return !!(
    params?.node?.aggData ||
    params?.data?.activity_name === 'Total' ||
    params?.data?.cost_category === 'Discount'
  );
};

// eslint-disable-next-line max-lines-per-function
export const actualsToDateColumnDef = (
  visible_columns: VisibleColumns['actuals_to_date'],
  selectedBudgetCurrencyType: BudgetCurrencyType
): ColDef | ColGroupDef => ({
  children: [
    {
      cellClass: [
        'ag-cell-align-right',
        'budget-units',
        AuxExcelStyleKeys.BORDER_LEFT,
        !visible_columns.costs && !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      columnGroupShow: 'closed',
      field: 'wp_unit_num',
      headerClass: [
        'ag-header-align-center',
        AuxExcelStyleKeys.BORDER_LEFT,
        !visible_columns.costs && !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      headerName: 'Unit(s)',
      hide: !visible_columns.units,
      minWidth: CellSize.medium,
      valueFormatter: (params) => {
        const bool = shouldHideUnits(params);

        if (bool) {
          return Utils.zeroHyphen;
        }

        return Utils.decimalFormatter(params.data?.wp_unit_num);
      },
      width: CellSize.medium,
    },
    {
      aggFunc: 'sum',
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
        !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      columnGroupShow: 'closed',
      field: 'wp_cost',
      headerClass: [
        'ag-header-align-center',
        !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
        !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      headerName: '$',
      hide: !visible_columns.costs,
      minWidth: CellSize.xLarge,
      valueFormatter: agBudgetCurrencyFormatter(selectedBudgetCurrencyType),
      width: CellSize.xLarge,
    },
    {
      cellClass: [
        'ag-cell-align-right',
        'budget-percent',
        !visible_columns.units && !visible_columns.costs ? AuxExcelStyleKeys.BORDER_LEFT : '',
        AuxExcelStyleKeys.BORDER_RIGHT,
      ],
      columnGroupShow: 'closed',
      field: 'wp_percentage',
      headerClass: [
        'ag-header-align-center',
        !visible_columns.units && !visible_columns.costs ? AuxExcelStyleKeys.BORDER_LEFT : '',
        AuxExcelStyleKeys.BORDER_RIGHT,
      ],
      headerName: '%',
      hide: !visible_columns.perc,
      minWidth: CellSize.small,
      valueFormatter: ({ value }) => Utils.percentageFormatter(value / 100),
      width: CellSize.small,
    },
  ],
  colId: 'WP',
  headerClass: ['ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark'],
  headerName: 'Actuals to Date',
});

export const rowGroupsColumnDef: ((ColDef | ColGroupDef) & {
  children?: (ColDef & { hideForAllVendorSelection?: boolean })[];
  hideForAllVendorSelection?: boolean;
})[] = [
  {
    cellClass: [AuxExcelStyleKeys.BORDER_LEFT],
    field: 'activity_no',
    headerClass: [AuxExcelStyleKeys.BORDER_LEFT],
    headerName: 'Activity ID',
    hide: true,
    hideForAllVendorSelection: true,
  },
  {
    field: 'vendor_name',
    headerName: 'Vendor Contract',
    hide: true,
    rowGroup: true,
  },
  {
    field: 'cost_category',
    headerName: 'Cost Category',
    hide: true,
    hideForAllVendorSelection: true,
    rowGroup: true,
  },
  {
    field: 'group0',
    headerName: 'Category',
    hide: true,
    hideForAllVendorSelection: true,
    rowGroup: true,
  },
  {
    field: 'display_label',
    headerName: 'Label',
    hide: true,
    hideForAllVendorSelection: true,
  },
  {
    field: 'activity_name_label',
    headerName: 'Activities',
    hide: true,
    hideForAllVendorSelection: true,
    tooltipField: 'activity_name',
    valueFormatter: Utils.dashFormatter,
  },
  {
    field: 'group1',
    headerName: 'Category',
    hide: true,
    hideForAllVendorSelection: true,
    rowGroup: true,
  },
  {
    field: 'group2',
    headerName: 'Category',
    hide: true,
    hideForAllVendorSelection: true,
    rowGroup: true,
  },
  {
    field: 'group3',
    headerName: 'Category',
    hide: true,
    hideForAllVendorSelection: true,
    rowGroup: true,
  },
  {
    field: 'group4',
    headerName: 'Category',
    hide: true,
    hideForAllVendorSelection: true,
    rowGroup: true,
  },
  {
    aggFunc: 'sum',
    field: `${ExpenseType.EXPENSE_QUOTE}::LATEST`,
    hide: true,
  },
  {
    aggFunc: 'sum',
    field: `${ExpenseType.EXPENSE_WP}::TO_DATE`,
    hide: true,
  },
  {
    aggFunc: 'first',
    field: 'contract_direct_cost_currency',
    hide: true,
  },
];

const getVarOverallBudgetPercent = (
  params: ValueFormatterParams | ValueGetterParams,
  compareToSelected?: string
) => {
  let percent = params.data?.var_percent;

  if (params.node?.aggData) {
    const { aggData } = params.node;
    const budgetSum = (compareToSelected ? aggData.snapshot_lre : aggData.baseline) || 0;
    const current_lre = aggData.current_lre || 0;
    const var_amount = current_lre - budgetSum;
    let var_percent = 0;

    if (budgetSum) {
      var_percent = budgetSum ? (var_amount / Math.abs(budgetSum)) * 100 : -100;
    }

    percent = round(var_percent, 2);
  }

  return percent;
};

// eslint-disable-next-line max-lines-per-function, complexity
export const overallBudgetColumnDef = (
  visible_columns: VisibleColumns['overall_budget'],
  selectedBudgetCurrencyType: BudgetCurrencyType,
  compareToSelected?: string
): ColDef | ColGroupDef => ({
  children: [
    {
      cellClass: [
        'text-left',
        'max-w',
        'whitespace-nowrap',
        'overflow-hidden',
        'text-ellipsis',
        TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT,
        AuxExcelStyleKeys.BORDER_LEFT,
      ],
      colId: 'uom',
      field: 'uom',
      filter: true,
      filterParams: {
        showTooltips: true,
      } as ISetFilterParams,
      headerClass: ['ag-header-align-center', AuxExcelStyleKeys.BORDER_LEFT],
      headerName: 'Unit of Measure',
      hide: uomHide$.getValue(),
      menuTabs: ['filterMenuTab'],
      minWidth: CellSize.large,
      suppressMenu: false,
      tooltipField: 'uom',
      valueFormatter: (params) => params.value || '',
      width: CellSize.large,
    },
    {
      cellClass: [
        'ag-cell-align-right',
        'budget-units',
        uomHide$.getValue() ? AuxExcelStyleKeys.BORDER_LEFT : '',
      ],
      field: 'unit_num',
      headerClass: [
        'ag-header-align-center',
        uomHide$.getValue() ? AuxExcelStyleKeys.BORDER_LEFT : '',
      ],
      headerName: 'Unit(s)',
      hide: !visible_columns.units,
      minWidth: CellSize.large,
      valueFormatter: (params) => {
        const bool = shouldHideUnits(params);

        if (bool) {
          return Utils.zeroHyphen;
        }

        return Utils.decimalFormatter(params.data?.unit_num);
      },
      width: CellSize.large,
    },
    {
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        uomHide$.getValue() && !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
      ],
      field:
        selectedBudgetCurrencyType === BudgetCurrencyType.VENDOR
          ? 'contract_unit_cost'
          : 'unit_cost',
      headerClass: [
        'ag-header-align-center',
        uomHide$.getValue() && !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
      ],
      headerName: 'Unit Cost',
      hide: !visible_columns.unit_cost,
      minWidth: CellSize.xLarge,
      valueFormatter: (params) => {
        const bool = shouldHideUnits(params);

        if (bool) {
          return Utils.zeroHyphen;
        }

        return agBudgetCurrencyFormatter(selectedBudgetCurrencyType)(params);
      },
      width: CellSize.xLarge,
    },
    {
      aggFunc: 'sum',
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        uomHide$.getValue() && !visible_columns.units && !visible_columns.unit_cost
          ? AuxExcelStyleKeys.BORDER_LEFT
          : '',
      ],
      field: 'current_lre',
      headerClass: [
        'ag-header-align-center',
        uomHide$.getValue() && !visible_columns.units && !visible_columns.unit_cost
          ? AuxExcelStyleKeys.BORDER_LEFT
          : '',
      ],
      headerName: 'Current (LRE)',
      hide: !visible_columns.primary,
      minWidth: CellSize.xLarge,
      valueFormatter: agBudgetCurrencyFormatter(selectedBudgetCurrencyType),
      width: CellSize.xLarge,
    },
    {
      ...TableConstants.dynamicColumnProps(compareToSelected || ''),
      aggFunc: 'sum',
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        uomHide$.getValue() &&
        !visible_columns.units &&
        !visible_columns.unit_cost &&
        !visible_columns.primary
          ? AuxExcelStyleKeys.BORDER_LEFT
          : '',
        !visible_columns.original && !visible_columns.var_cost && !visible_columns.var_perc
          ? AuxExcelStyleKeys.BORDER_RIGHT
          : '',
      ],
      field: 'snapshot_lre',
      headerClass: [
        uomHide$.getValue() &&
        !visible_columns.units &&
        !visible_columns.unit_cost &&
        !visible_columns.primary
          ? AuxExcelStyleKeys.BORDER_LEFT
          : '',
        !visible_columns.original && !visible_columns.var_cost && !visible_columns.var_perc
          ? AuxExcelStyleKeys.BORDER_RIGHT
          : '',
      ],
      hide: !compareToSelected,
      minWidth: CellSize.xLarge,
      valueFormatter: agBudgetCurrencyFormatter(selectedBudgetCurrencyType),
    },
    {
      aggFunc: 'sum',
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        uomHide$.getValue() &&
        !visible_columns.units &&
        !visible_columns.unit_cost &&
        !visible_columns.primary &&
        !compareToSelected
          ? AuxExcelStyleKeys.BORDER_LEFT
          : '',
        !visible_columns.var_cost && !visible_columns.var_perc
          ? AuxExcelStyleKeys.BORDER_RIGHT
          : '',
      ],
      field: 'baseline',
      headerClass: [
        'ag-header-align-center',
        uomHide$.getValue() &&
        !visible_columns.units &&
        !visible_columns.unit_cost &&
        !visible_columns.primary &&
        !compareToSelected
          ? AuxExcelStyleKeys.BORDER_LEFT
          : '',
        !visible_columns.var_cost && !visible_columns.var_perc
          ? AuxExcelStyleKeys.BORDER_RIGHT
          : '',
      ],
      headerName: 'Baseline',
      hide: !visible_columns.original || !!compareToSelected,
      minWidth: CellSize.xLarge,
      valueFormatter: agBudgetCurrencyFormatter(selectedBudgetCurrencyType),
      width: CellSize.xLarge,
    },
    {
      aggFunc: 'sum',
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        !visible_columns.var_perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      cellRenderer: VariationStatusComponent,
      field: `var_amount`,
      headerClass: [
        'ag-header-align-center',
        !visible_columns.var_perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      headerName: 'Var ($)',
      hide: !visible_columns.var_cost,
      minWidth: CellSize.xLarge,
      valueFormatter: agBudgetCurrencyFormatter(selectedBudgetCurrencyType),
      width: CellSize.xLarge,
    },
    {
      cellClass: ['ag-cell-align-right', 'budget-percent-no-mult', AuxExcelStyleKeys.BORDER_RIGHT],
      cellRenderer: VariationStatusComponent,
      field: `var_percent`,
      headerClass: ['ag-header-align-center', AuxExcelStyleKeys.BORDER_RIGHT],
      headerName: 'Var (%)',
      hide: !visible_columns.var_perc,
      minWidth: CellSize.medium,
      valueFormatter: (params) =>
        percentageFormatter(Math.abs(getVarOverallBudgetPercent(params, compareToSelected))),
      valueGetter: (params) => getVarOverallBudgetPercent(params, compareToSelected),
      width: CellSize.medium,
    },
  ],
  headerClass: [
    'ag-header-align-center bg-aux-gray-dark gray-dark-header-group aux-black border-0 border-b-aux-gray-dark border-b-3',
  ],
  headerName: 'Overall Budget',
});

// eslint-disable-next-line max-lines-per-function
export const remainingBudgetColDef = (
  visible_columns: VisibleColumns['remaining_budget'],
  selectedBudgetCurrencyType: BudgetCurrencyType
): ColDef | ColGroupDef => ({
  children: [
    {
      cellClass: [
        'ag-cell-align-right',
        'budget-units',
        AuxExcelStyleKeys.BORDER_LEFT,
        !visible_columns.costs && !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      field: 'remaining_unit_num',
      headerClass: [
        'ag-header-align-center',
        AuxExcelStyleKeys.BORDER_LEFT,
        !visible_columns.costs && !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      headerName: 'Unit(s)',
      hide: !visible_columns.units,
      minWidth: CellSize.medium,
      valueFormatter: (params) => {
        const bool = shouldHideUnits(params);

        if (bool) {
          return Utils.zeroHyphen;
        }

        return Utils.decimalFormatter(params.data?.remaining_unit_num);
      },
      width: CellSize.medium,
    },
    {
      aggFunc: 'sum',
      cellClass: (p) => [
        getCellClass(selectedBudgetCurrencyType)(p),
        !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
        !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      field: 'remaining_cost',
      headerClass: [
        // eslint-disable-next-line max-lines
        'ag-header-align-center',
        !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
        !visible_columns.perc ? AuxExcelStyleKeys.BORDER_RIGHT : '',
      ],
      headerName: '$',
      hide: !visible_columns.costs,
      minWidth: CellSize.xLarge,
      valueFormatter: agBudgetCurrencyFormatter(selectedBudgetCurrencyType),
      width: CellSize.xLarge,
    },
    {
      cellClass: [
        'ag-cell-align-right',
        'budget-percent',
        !visible_columns.costs && !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
        AuxExcelStyleKeys.BORDER_RIGHT,
      ],
      field: 'remaining_percentage',
      headerClass: [
        'ag-header-align-center',
        !visible_columns.costs && !visible_columns.units ? AuxExcelStyleKeys.BORDER_LEFT : '',
        AuxExcelStyleKeys.BORDER_RIGHT,
      ],
      headerName: '%',
      hide: !visible_columns.perc,
      minWidth: CellSize.small,
      valueFormatter: ({ value }) => Utils.percentageFormatter(value / 100),
      width: CellSize.small,
    },
  ],
  headerClass: [
    'ag-header-align-center bg-aux-gray-dark aux-black gray-dark-header-group border-0',
  ],
  headerName: 'Remaining Budget',
});
